import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { 
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RestartIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const TenantDockerManagement = () => {
  const { tenantId } = useParams();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState(null);

  useEffect(() => {
    // TODO: Load containers from API
    setContainers([
      {
        id: 'container1',
        name: 'web-app',
        image: 'nginx:latest',
        status: 'running',
        ports: ['80:80', '443:443'],
        memory: 512,
        cpu: 25,
        created: '2024-01-01T10:00:00Z'
      },
      {
        id: 'container2',
        name: 'api-service',
        image: 'node:18',
        status: 'stopped',
        ports: ['3000:3000'],
        memory: 1024,
        cpu: 0,
        created: '2024-01-01T10:00:00Z'
      }
    ]);
  }, []);

  const handleContainerAction = (containerId, action) => {
    setLoading(true);
    // TODO: Implement container actions
    console.log(`${action} container:`, containerId);
    setTimeout(() => setLoading(false), 1000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'error';
      case 'starting':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 MB';
    return `${bytes} MB`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Docker Container Yönetimi - {tenantId}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Yeni Container
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Container List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Container'lar
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>İsim</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Portlar</TableCell>
                      <TableCell>Bellek</TableCell>
                      <TableCell>CPU</TableCell>
                      <TableCell>Oluşturulma</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {containers.map((container) => (
                      <TableRow key={container.id}>
                        <TableCell>{container.name}</TableCell>
                        <TableCell>{container.image}</TableCell>
                        <TableCell>
                          <Chip 
                            label={container.status} 
                            color={getStatusColor(container.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {container.ports.map((port, index) => (
                            <Chip key={index} label={port} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>{formatBytes(container.memory)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={container.cpu} 
                              sx={{ width: 60 }}
                            />
                            <Typography variant="body2">{container.cpu}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(container.created).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {container.status === 'running' ? (
                              <Tooltip title="Durdur">
                                <IconButton 
                                  onClick={() => handleContainerAction(container.id, 'stop')}
                                  disabled={loading}
                                >
                                  <StopIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Başlat">
                                <IconButton 
                                  onClick={() => handleContainerAction(container.id, 'start')}
                                  disabled={loading}
                                >
                                  <StartIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Yeniden Başlat">
                              <IconButton 
                                onClick={() => handleContainerAction(container.id, 'restart')}
                                disabled={loading}
                              >
                                <RestartIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Sil">
                              <IconButton 
                                onClick={() => handleContainerAction(container.id, 'delete')}
                                disabled={loading}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Resources */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MemoryIcon />
                <Typography variant="h6">Sistem Kaynakları</Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Toplam Bellek Kullanımı</Typography>
                  <Typography variant="body2">1.5 GB / 8 GB</Typography>
                </Box>
                <LinearProgress variant="determinate" value={18.75} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Toplam CPU Kullanımı</Typography>
                  <Typography variant="body2">25%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={25} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Disk Kullanımı</Typography>
                  <Typography variant="body2">50 GB / 500 GB</Typography>
                </Box>
                <LinearProgress variant="determinate" value={10} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hızlı İşlemler
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<StartIcon />}
                    onClick={() => handleContainerAction('all', 'start-all')}
                    disabled={loading}
                  >
                    Tümünü Başlat
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<StopIcon />}
                    onClick={() => handleContainerAction('all', 'stop-all')}
                    disabled={loading}
                  >
                    Tümünü Durdur
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RestartIcon />}
                    onClick={() => handleContainerAction('all', 'restart-all')}
                    disabled={loading}
                  >
                    Tümünü Yeniden Başlat
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Ayarlar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* New Container Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Container Oluştur</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Container İsmi"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Docker Image"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Port Mapping"
            margin="normal"
            placeholder="80:80,443:443"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>İptal</Button>
          <Button variant="contained">Oluştur</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantDockerManagement;
